import {
  TbAbc,
  TbArrowsLeftRight,
  TbClock,
  TbCloud,
  TbDecimal,
  TbFileDigit,
  TbHash,
  TbHeartRateMonitor,
  TbList,
  TbNetwork,
  TbPercentage,
  TbPlugConnected,
  TbRoute,
  TbServer,
  TbTag,
  TbTemperature,
  TbTopologyRing,
  TbWorld,
  TbWorldCode,
  TbWorldWww
} from 'react-icons/tb';
import { IconType } from 'react-icons';
import { MeasurementDimension, MeasurementMetric } from '@kentik/ui-shared/query/query.types';

import { isEnumItem } from '@kentik/ui-shared/query/enumUtils';

const DURATION_UNITS = new Set([
  'duration:microseconds',
  'microseconds',
  'microsecond',
  'duration',
  's',
  'second',
  'seconds',
  'milliseconds',
  'minutes'
]);

const RATE_UNITS = new Set([
  'bits/s',
  'bps',
  'bits/sec',
  'mbits/s',
  'mbps',
  'packets/s',
  'packets/sec',
  'pps',
  'flows/s',
  'bytes/second'
]);

/**
 * Returns an icon component for a metric based on its type metadata.
 * Priority: enum (with special unit) → unit-specific → Type field → inferred → fallback
 */
export function getMetricIcon(metric: MeasurementMetric | undefined): IconType {
  if (!metric) {
    return TbTag;
  }

  const unit = metric.Unit?.toLowerCase();
  const isEnum = !!metric.Values && Object.keys(metric.Values).length > 0;

  // Special enum-style units (apply whether or not Values is set)
  if (unit === 'health') {
    return TbHeartRateMonitor;
  }
  if (unit === 'dns_code') {
    return TbWorldCode;
  }
  if (unit === 'http_status_code') {
    return TbWorldWww;
  }

  // Generic enum
  if (isEnum) {
    return TbList;
  }

  if (unit) {
    if (DURATION_UNITS.has(unit)) {
      return TbClock;
    }
    if (unit === 'percent' || unit === '%') {
      return TbPercentage;
    }
    if (RATE_UNITS.has(unit)) {
      return TbArrowsLeftRight;
    }
    if (unit === 'bytes' || unit === 'b') {
      return TbFileDigit;
    }
    if (unit === 'packets') {
      return TbHash;
    }
    if (unit === 'celsius') {
      return TbTemperature;
    }
    if (unit === 'connections') {
      return TbPlugConnected;
    }
  }

  // Inferred rate from ToBits + rate window
  if (metric.ToBits && metric.Window === 'rate') {
    return TbArrowsLeftRight;
  }

  if (metric.Type === 'float') {
    return TbDecimal;
  }
  if (metric.Type === 'int') {
    return TbHash;
  }

  return TbTag;
}

const GEO_PATTERNS: RegExp[] = [
  /_geo$/i,
  /_geo_/i,
  /^geography/i,
  /^src_geo/i,
  /^dst_geo/i,
  /country/i,
  /(^|_)region(_|$)/i,
  /(^|_)city(_|$)/i
];

const CLOUD_CATEGORIES = new Set([
  'amazon web services',
  'microsoft azure',
  'google cloud platform',
  'oracle cloud infrastructure'
]);

/**
 * Returns an icon component for a dimension based on its type metadata, key, and category.
 * Priority: inet type → geo key → percent unit → cloud → topology → routing → vendor → Type → fallback
 */
export function getDimensionIcon(dim: MeasurementDimension | undefined, dimKey?: string, category?: string): IconType {
  if (!dim) {
    return TbTag;
  }

  if (dim.Type === 'inet') {
    return TbNetwork;
  }

  if (dimKey && GEO_PATTERNS.some((p) => p.test(dimKey))) {
    return TbWorld;
  }

  const dimUnit = dim.Unit?.toLowerCase();
  if (dimUnit === 'percent' || dimUnit === '%') {
    return TbPercentage;
  }

  if (category) {
    const normalizedCategory = category.toLowerCase();
    if (CLOUD_CATEGORIES.has(normalizedCategory)) {
      return TbCloud;
    }
    if (normalizedCategory === 'network & traffic topology') {
      return TbTopologyRing;
    }
    if (normalizedCategory.includes('bgp') || normalizedCategory.includes('routing')) {
      return TbRoute;
    }
    if (normalizedCategory.includes('vendor') || normalizedCategory.includes('device')) {
      return TbServer;
    }
  }

  if (isEnumItem(dim)) {
    return TbList;
  }

  if (dim.Type === 'int' || dim.Type === 'uint32') {
    return TbHash;
  }
  if (dim.Type === 'string') {
    return TbAbc;
  }

  return TbAbc;
}
